import { isOverlayEditableObject } from "../editor/textObject";
import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { BoardCallSplitLayout } from "../components/board/BoardCallSplitLayout";
import { useBoardCallSession } from "../hooks/useBoardCallSession";
import { BoardCollaborationBar } from "../components/board/BoardCollaborationBar";
import type { BoardParticipant } from "../components/board/BoardParticipants";
import { BoardEmptyHint } from "../components/board/BoardEmptyHint";
import { BoardFloatingToolbar } from "../components/board/BoardFloatingToolbar";
import { BoardPageMenu } from "../components/board/BoardPageMenu";
import {
  BoardCheckpointsPanel,
  type BoardCheckpointItem,
} from "../components/board/BoardCheckpointsPanel";
import { BoardHelpButton } from "../components/board/BoardHelpButton";
import { BoardTour } from "../components/board/BoardTour";
import { BoardSettingsDialog } from "../components/board/BoardSettingsDialog";
import { BoardFramesButton } from "../components/board/BoardFramesButton";
import { BoardInfoDialog } from "../components/board/BoardInfoDialog";
import { BoardEndSessionDialog } from "../components/board/BoardEndSessionDialog";
import { BoardFramePresentBar } from "../components/board/BoardFramePresentBar";
import { BoardVotePanel } from "../components/board/BoardVotePanel";
import { BoardCopyShareLinkButton } from "../components/board/BoardCopyShareLinkButton";
import { useFramePresentation, type BoardSignalEvent } from "../hooks/useFramePresentation";
import { useBoardVote, VOTE_HEIGHT_DEFAULT, VOTE_WIDTH_DEFAULT } from "../hooks/useBoardVote";
import { useSpaceGesture } from "../hooks/useSpaceGesture";
import { cycleActivePenDash } from "../editor/penDash";
import { frameTitle, isFrameObject } from "../editor/frame/frameContext";
import { BoardSchoolPromoStrip } from "../components/board/BoardSchoolPromoStrip";
import { BoardTourPromoStrip } from "../components/board/BoardTourPromoStrip";
import { BoardLessonFinishStrip } from "../components/board/BoardLessonFinishStrip";
import { BoardBugReportDialog } from "../components/board/BoardBugReportDialog";
import { noteDiagBoardError, setBoardDiagSceneGetter } from "../lib/board/boardDiagTrail";
import { useBoardShareLinkCopy } from "../hooks/useBoardShareLinkCopy";
import { BoardSelectionToolbar } from "../components/board/BoardSelectionToolbar";
import { TextOverlayEditor } from "../components/board/TextOverlayEditor";
import { ObjectCommentsPanel } from "../components/board/ObjectCommentsPanel";
import { PresentationOverlay } from "../components/board/PresentationOverlay";
import { usePdfPresentation } from "../hooks/usePdfPresentation";
import { PdfImportProgress } from "../components/board/PdfImportProgress";
import type { BoardToolId } from "../editor/types";
import { copyStyleFromSelection, pasteStyleToSelection } from "../editor/styleClipboard";
import { lineAxisSnapEnabled, setLineAxisSnapEnabled } from "../editor/lineSnapPrefs";
import { useBoardToolShortcuts, isTypingTarget } from "../hooks/useBoardToolShortcuts";
import { FlashStack, useFlashStack } from "../components/FlashStack";
import { useBoardViewport } from "../hooks/useBoardViewport";
import { useBoardChromeQuiet } from "../hooks/useBoardChromeQuiet";
import { useFollowPresenter } from "../hooks/useFollowPresenter";
import { BoardZoomControls } from "../components/board/BoardZoomControls";
import { BoardMinimap, contentBoundsForFit } from "../components/board/BoardMinimap";
import {
  readMinimapVisibleDefault,
  writeMinimapVisible,
} from "../lib/board/minimapPrefs";
import {
  apiFetch,
  ApiError,
  rememberBoardByLink,
  type BoardLoadResponse,
  type MeResponse,
} from "../api/client";
import {
  boardLoadErrorMessage,
  fetchAdminSpectateBoard,
} from "../lib/board/adminSpectateBoard";
import type { BoardObjectV2, BoardTemplateId } from "@bezpart-board/shared";
import { buildWidgetObjectsAtCenter } from "../lib/board/insertBoardWidget";
import {
  boundsForSelection,
  boundsForViewport,
  downloadBlob,
  exportBoardPng,
  exportFilename,
} from "../lib/board/exportBoardPng";
import { boardWsStatusMessage } from "../lib/board/boardErrors";
import { WbBoardScene } from "../lib/board/wb-board-scene";
import { handleBoardClipboardPaste } from "../lib/board/handleBoardClipboardPaste";
import { handleBoardClipboardCopy } from "../lib/board/handleBoardClipboardCopy";
import { WbBoardPresence } from "../lib/board/wb-board-presence";
import { isBoardAudioFile } from "../lib/audio/importAudioToBoard";
import {
  isPresentationFile,
  presentationProgressLabel,
} from "../lib/pdf/importPresentationToBoard";
import { downloadPresentationPdf } from "../lib/pdf/downloadPresentationPdf";
import { authEntryPathForError } from "../lib/authRedirect";

type WsState =
  | "connecting"
  | "connected"
  | "subscribed"
  | "reconnecting"
  | "disconnected"
  // Доску перехватила другая вкладка того же человека.
  | "replaced"
  | "error";

export function BoardPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const stageRef = useRef<HTMLDivElement>(null);
  const boardMainRef = useRef<HTMLElement | null>(null);
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const sceneRef = useRef<InstanceType<typeof WbBoardScene> | null>(null);
  const presenceRef = useRef<InstanceType<typeof WbBoardPresence> | null>(null);
  const flash = useFlashStack();

  const [title, setTitle] = useState("Доска");
  const [description, setDescription] = useState("");
  /** Название и описание меняет только владелец доски. */
  const [canEditInfo, setCanEditInfo] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  /** «Завершить сессию»: сохранить урок и выйти в кабинет. */
  const [endSessionOpen, setEndSessionOpen] = useState(false);
  /**
   * Доска — урок школы: у школы своё «Завершить занятие» с итогами,
   * общая «Завершить сессию» там лишняя и путала бы преподавателя.
   */
  const [schoolLesson, setSchoolLesson] = useState(false);
  /** Сигналы урока (просьба ученика уйти, ответ, «занятие проведено»). */
  const lessonSignalRef = useRef<((ev: BoardSignalEvent) => void) | null>(null);
  /** Интерактивное знакомство с доской. Запускается кнопкой, само не лезет. */
  const [tourOpen, setTourOpen] = useState(false);
  const [ready, setReady] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [tool, setTool] = useState<BoardToolId>("select");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isSelectionDragging, setIsSelectionDragging] = useState(false);
  const [chromeLayoutTick, setChromeLayoutTick] = useState(0);
  const [historyTick, setHistoryTick] = useState(0);
  const [canSyncToolPresets, setCanSyncToolPresets] = useState(false);
  const [textEditId, setTextEditId] = useState<string | null>(null);
  const [commentObjectId, setCommentObjectId] = useState<string | null>(null);
  const pdfPresentation = usePdfPresentation(sceneRef, ready);
  const [pdfImportMessage, setPdfImportMessage] = useState<string | null>(null);
  const [exportBusy, setExportBusy] = useState(false);
  const [showAccess, setShowAccess] = useState(false);
  const [accountKind, setAccountKind] = useState<"school" | "external" | null>(null);
  const [myUserId, setMyUserId] = useState<string | null>(null);
  const [planCode, setPlanCode] = useState<string | null>(null);
  const [sessionAccess, setSessionAccess] = useState(false);
  const [participants, setParticipants] = useState<BoardParticipant[]>([]);
  const [wsState, setWsState] = useState<WsState>("disconnected");
  const [wsDetail, setWsDetail] = useState<string | undefined>();
  const [helpOpen, setHelpOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [bugReportOpen, setBugReportOpen] = useState(false);
  const [accessPanelOpen, setAccessPanelOpen] = useState(false);
  const [minimapVisible, setMinimapVisible] = useState(readMinimapVisibleDefault);
  const [compactHeader, setCompactHeader] = useState(false);
  const [checkpointsOpen, setCheckpointsOpen] = useState(false);
  const [checkpointBusy, setCheckpointBusy] = useState(false);
  const [pendingRestore, setPendingRestore] = useState<BoardCheckpointItem | null>(null);
  const [restoreSaveTitle, setRestoreSaveTitle] = useState("");
  const toolbarRef = useRef<HTMLElement>(null);
  const shareLink = useBoardShareLinkCopy(id ?? "", showAccess);

  useEffect(() => {
    const el = toolbarRef.current;
    if (!el) return;

    const update = () => {
      // Компакт, если окно узкое или сама шапка уже тесная (сплит / боковая панель)
      const narrowViewport = window.matchMedia("(max-width: 1200px)").matches;
      setCompactHeader(narrowViewport || el.clientWidth < 980);
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  const vp = useBoardViewport();
  useBoardChromeQuiet(boardMainRef, ready);
  const vpRef = useRef(vp);
  vpRef.current = vp;
  const follow = useFollowPresenter(vp, presenceRef, ready, participants);
  const onPeerPresenceRef = useRef(follow.onPeerPresence);
  onPeerPresenceRef.current = follow.onPeerPresence;
  const onSignalRef = useRef<(ev: { kind: string; payload: Record<string, unknown>; peerId: string | null; displayLabel: string | null; ts: number; isSelf: boolean }) => void>(() => {});
  const onPeerJoinedRef = useRef<() => void>(() => {});
  const onSubscribedRef = useRef<() => void>(() => {});
  const lastBoardErrorToastRef = useRef<{ key: string; at: number } | null>(null);

  const pushBoardError = useCallback(
    (msg: string) => {
      const now = Date.now();
      const prev = lastBoardErrorToastRef.current;
      if (prev && prev.key === msg && now - prev.at < 10_000) return;
      lastBoardErrorToastRef.current = { key: msg, at: now };
      const codeMatch = msg.match(/\[code=([^ ·\]]+)/);
      const ctxMatch = msg.match(/\[([^ ·\]]+?) · code=/);
      if (codeMatch) {
        noteDiagBoardError({
          text: msg,
          code: codeMatch[1],
          context: ctxMatch ? ctxMatch[1] : undefined,
        });
      }
      flash.push("error", msg);
    },
    [flash]
  );

  const handleDownloadPresentation = useCallback(
    async (objectId: string) => {
      const scene = sceneRef.current;
      if (!scene) return;
      try {
        await downloadPresentationPdf(scene, objectId);
        flash.push("ok", "Презентация сохранена");
      } catch (e) {
        flash.push("error", e instanceof Error ? e.message : "Не удалось скачать презентацию");
      }
    },
    [flash]
  );

  const shareToken = searchParams.get("t");
  const adminSpectateQuery = searchParams.get("spectate") === "1";
  const callOpenParam = searchParams.get("callOpen") === "1";
  /**
   * Токен ссылки для звонка запоминаем при открытии. Доска потом может убрать
   * `?t=` из адреса (у человека нашёлся свой доступ) — тогда второй запрос
   * пошёл бы без токена, отменил первый и выдал ученику ссылку преподавателя
   * или не выдал ничего, и панель открыла бы главную страницу звонилки.
   */
  const [callShareToken] = useState(() => searchParams.get("t"));
  const callSession = useBoardCallSession(
    !id || adminSpectateQuery
      ? null
      : `/api/v1/boards/${encodeURIComponent(id)}/call-session${
          callShareToken ? `?t=${encodeURIComponent(callShareToken)}` : ""
        }`
  );
  const [adminSpectate, setAdminSpectate] = useState(false);
  const isFreeExternal = accountKind === "external" && (planCode ?? "free") === "free";

  useEffect(() => {
    setBoardDiagSceneGetter(() => {
      const scene = sceneRef.current as
        | (NonNullable<typeof sceneRef.current> & {
            store?: { getRevision?: () => number };
          })
        | null;
      if (!scene) return null;
      try {
        return {
          revision: scene.store?.getRevision?.() ?? 0,
          objects: scene.getObjects() ?? [],
        };
      } catch {
        return null;
      }
    });
    return () => setBoardDiagSceneGetter(null);
  }, [ready, id]);

  /**
   * Хозяин доски — тот, кому сервер разрешил менять её название
   * (edit_info выдаётся только владельцу).
   *
   * Раньше полоски смотрели на accessMode === "owner", но вошедшему
   * преподавателю сервер отвечает access: "session", и accessMode
   * становится "session" — «owner» не наступал никогда. Из-за этого не
   * появлялась кнопка «Завершить занятие» и молчали рекламные полоски.
   */
  const isBoardOwner = canEditInfo && !adminSpectate && !shareToken;

  const accessMode = adminSpectate
    ? "admin_spectate"
    : !canEdit
      ? "view_only"
      : shareToken
        ? "share"
        : sessionAccess
          ? "session"
          : "owner";

  useEffect(() => {
    let cancelled = false;
    void apiFetch<MeResponse>("/api/v1/me")
      .then((me) => {
        if (cancelled) return;
        setAccountKind(me.account_kind === "school" ? "school" : "external");
        setPlanCode(me.plan?.code ?? null);
        setMyUserId(typeof me.id === "string" ? me.id : null);
      })
      .catch(() => {
        if (cancelled) return;
        setAccountKind(null);
        setPlanCode(null);
        setMyUserId(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const saveCheckpoint = useCallback(
    async (title: string) => {
      if (!id || checkpointBusy) return;
      if (isFreeExternal) {
        flash.push("error", "Снимки доски доступны на Pro.");
        throw new Error("plan_feature_checkpoints");
      }
      setCheckpointBusy(true);
      try {
        await apiFetch(`/api/v1/boards/${encodeURIComponent(id)}/checkpoints`, {
          method: "POST",
          json: { title },
        });
        flash.push("ok", `Снимок «${title}» сохранён`);
      } catch {
        pushBoardError("Не удалось сохранить снимок");
        throw new Error("checkpoint_save_failed");
      } finally {
        setCheckpointBusy(false);
      }
    },
    [id, checkpointBusy, flash, pushBoardError, isFreeExternal]
  );

  const restoreCheckpoint = useCallback(
    async (checkpointId: string) => {
      if (!id || checkpointBusy) return;
      const scene = sceneRef.current;
      if (!scene) return;
      setCheckpointBusy(true);
      try {
        const data = await apiFetch<{
          snapshot?: { revision: number; scene: unknown };
        }>(
          `/api/v1/boards/${encodeURIComponent(id)}/checkpoints/${encodeURIComponent(checkpointId)}/restore`,
          { method: "POST", json: {} }
        );
        if (data.snapshot) {
          scene.applySnapshot(data.snapshot, { remote: true });
        } else {
          await scene.reloadFromServer();
        }
        flash.push("ok", "Доска возвращена к снимку");
        setPendingRestore(null);
        setRestoreSaveTitle("");
        setCheckpointsOpen(false);
      } catch {
        pushBoardError("Не удалось вернуться к снимку");
        throw new Error("checkpoint_restore_failed");
      } finally {
        setCheckpointBusy(false);
      }
    },
    [id, checkpointBusy, flash, pushBoardError]
  );

  const deleteCheckpoint = useCallback(
    async (checkpointId: string) => {
      if (!id || checkpointBusy) return;
      setCheckpointBusy(true);
      try {
        await apiFetch(
          `/api/v1/boards/${encodeURIComponent(id)}/checkpoints/${encodeURIComponent(checkpointId)}`,
          { method: "DELETE" }
        );
        flash.push("ok", "Снимок удалён");
      } catch {
        pushBoardError("Не удалось удалить снимок");
        throw new Error("checkpoint_delete_failed");
      } finally {
        setCheckpointBusy(false);
      }
    },
    [id, checkpointBusy, flash, pushBoardError]
  );

  const confirmRestoreWithSave = useCallback(async () => {
    if (!pendingRestore) return;
    const title = restoreSaveTitle.trim();
    if (!title) return;
    try {
      await saveCheckpoint(title);
      await restoreCheckpoint(pendingRestore.id);
    } catch {
      /* ошибки уже показаны */
    }
  }, [pendingRestore, restoreSaveTitle, saveCheckpoint, restoreCheckpoint]);

  const confirmRestoreSkip = useCallback(async () => {
    if (!pendingRestore) return;
    try {
      await restoreCheckpoint(pendingRestore.id);
    } catch {
      /* ошибки уже показаны */
    }
  }, [pendingRestore, restoreCheckpoint]);

  useEffect(() => {
    document.body.classList.add("wb-page--board");
    return () => document.body.classList.remove("wb-page--board");
  }, []);

  useEffect(() => {
    if (!id || !stageRef.current) return;

    let cancelled = false;
    const stageEl = stageRef.current;
    // Читаем ?t= один раз при открытии доски — не завязываем effect на shareToken,
    // иначе очистка URL / ререндер снова уничтожает сцену (мигание).
    const initialShareToken = new URLSearchParams(window.location.search).get("t");
    const initialAdminSpectate =
      new URLSearchParams(window.location.search).get("spectate") === "1";

    function boardUrl(token?: string | null) {
      let url = `/api/v1/boards/${encodeURIComponent(id!)}`;
      if (token) url += `?t=${encodeURIComponent(token)}`;
      return url;
    }

    function mountBoard(
      data: BoardLoadResponse,
      accessOpts: {
        shareToken: string | null;
        useCredentials: boolean;
        adminSpectate?: boolean;
      }
    ) {
      if (cancelled) return;
      const t = data.board.title || "Доска";
      setTitle(t);
      setDescription(data.board.description ?? "");
      setCanEditInfo(Boolean(data.capabilities?.edit_info));
      document.title = `${t} — Bezpart Board`;

      const spectate =
        Boolean(accessOpts.adminSpectate) || data.access === "admin_spectate";
      setAdminSpectate(spectate);
      const edit = spectate ? false : Boolean(data.capabilities?.edit_snapshot);
      setCanEdit(edit);
      setCanSyncToolPresets(accessOpts.useCredentials && !spectate);
      setShowAccess(data.access === "session" && !spectate);
      setSessionAccess(data.access === "session" && !spectate);
      // Viewer: сразу «рука», чтобы не казалось, что объекты можно двигать.
      const initialTool: BoardToolId = edit ? "select" : "hand";
      setTool(initialTool);
      if (!edit) setSelectedIds([]);

      const revNum =
        data.snapshot && typeof data.snapshot.revision === "number"
          ? data.snapshot.revision
          : 0;

      setReady(true);

      const onError = (msg: string) => pushBoardError(msg);

      const boardScene = new WbBoardScene({
        stage: stageEl,
        boardId: data.board.id,
        shareToken: accessOpts.useCredentials ? null : accessOpts.shareToken,
        useCredentials: accessOpts.useCredentials,
        canEdit: edit,
        scene: data.snapshot?.scene,
        revision: revNum,
        mapClientToStage: vp.mapClientToStageStable,
        toolMode: initialTool,
        onSelectionChange: setSelectedIds,
        onSelectionChromeLayout: () => setChromeLayoutTick((n) => n + 1),
        onHistoryChange: () => setHistoryTick((n) => n + 1),
        onTextEditRequest: setTextEditId,
        onActiveToolChange: setTool,
        onRevision: () => {},
        panBy: vp.panBy,
        endPanGesture: vp.endPanGesture,
        onError,
      });
      boardScene.mount();
      sceneRef.current = boardScene;

      const presenceCanvas =
        stageEl.closest(".wb-board-viewport") ?? stageEl;
      const presence = new WbBoardPresence({
        boardId: data.board.id,
        shareToken:
          spectate || accessOpts.useCredentials ? null : accessOpts.shareToken,
        adminSpectate: spectate,
        silent: spectate,
        canEditOps: edit,
        canvas: presenceCanvas as HTMLDivElement,
        mapClientToStage: vp.mapClientToStageStable,
        getViewport: () => {
          const live = vpRef.current.getLiveViewport?.() ?? vpRef.current.viewport;
          return {
            panX: live.panX,
            panY: live.panY,
            scale: live.scale,
          };
        },
        onPeerPresence: (
          peerId: string,
          data: {
            viewport?: { panX: number; panY: number; scale: number } | null;
            cursor?: { x: number; y: number } | null;
          }
        ) => {
          onPeerPresenceRef.current(peerId, data);
        },
        onSignal: (ev: {
          peerId: string | null;
          displayLabel: string | null;
          kind: string;
          payload: Record<string, unknown>;
          ts: number;
          isSelf: boolean;
        }) => {
          onSignalRef.current(ev);
        },
        onPeerJoined: () => {
          onPeerJoinedRef.current();
        },
        onSubscribed: () => {
          onSubscribedRef.current();
        },
        onRosterChange: setParticipants,
        onPeersChange: () => {},
        onStatus: (state: string, detail?: string) => {
          setWsState(state as WsState);
          setWsDetail(detail);
          if (state === "error" && detail) {
            onError(boardWsStatusMessage(detail));
          }
        },
      });
      boardScene.bindRealtime(presence);
      presence.attach();
      presenceRef.current = presence;
    }

    async function loadFlow() {
      try {
        async function mountAdminSpectate(updateUrl: boolean) {
          const data = await fetchAdminSpectateBoard(id!);
          if (!data) {
            throw new ApiError("Доска не найдена или нет прав администратора.", 404);
          }
          if (updateUrl && !initialAdminSpectate) {
            navigate(`/board/${id}?spectate=1`, { replace: true });
          }
          mountBoard(data, {
            shareToken: null,
            useCredentials: true,
            adminSpectate: true,
          });
        }

        if (initialAdminSpectate) {
          await mountAdminSpectate(false);
          return;
        }

        const shareT = initialShareToken;
        if (shareT) {
          // Свой аккаунт важнее ссылки: хозяин доски и участник-редактор
          // заходят под своим именем и со своими правами, а не как гость.
          const ownAccess = await apiFetch<BoardLoadResponse>(boardUrl(null)).catch(() => null);
          const ownEditor =
            ownAccess?.access === "session" && Boolean(ownAccess.capabilities?.edit_snapshot);
          if (ownAccess && ownEditor) {
            // Токен из адреса убираем: иначе обновление страницы снова
            // увело бы в гостевой режим.
            navigate(`/board/${id}`, { replace: true });
            mountBoard(ownAccess, { shareToken: null, useCredentials: true });
            return;
          }

          const ex = await fetch(`/api/v1/boards/${encodeURIComponent(id)}/guest-session`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token: shareT }),
          }).then((r) => r.json().then((data) => ({ status: r.status, data })));

          // Сервер ответил, что доступ у пользователя и так есть по аккаунту.
          if (ex.status === 200 && ex.data?.code === "session_access") {
            const own = ownAccess ?? (await apiFetch<BoardLoadResponse>(boardUrl(null)));
            navigate(`/board/${id}`, { replace: true });
            mountBoard(own, { shareToken: null, useCredentials: true });
            return;
          }

          if (ex.status === 201) {
            // Вошедший в аккаунт оставляет доску себе: иначе она нигде
            // у него не числится и раздел «Со мной» остаётся пустым.
            void rememberBoardByLink(id!, shareT);
            mountBoard(ex.data as BoardLoadResponse, {
              shareToken: shareT,
              useCredentials: true,
            });
            return;
          }
          if (ex.status === 403 && ex.data?.code === "guest_policy_private") {
            // Доску открываем по самой ссылке, без куки сессии: иначе сервер
            // ответит как своему, а прав по аккаунту тут нет. Поэтому ярлык
            // просим отдельным запросом — он и ходит под аккаунтом.
            const data = await apiFetch<BoardLoadResponse>(boardUrl(shareT), {
              credentials: "omit",
            });
            void rememberBoardByLink(id!, shareT);
            mountBoard(data, { shareToken: shareT, useCredentials: false });
            return;
          }
          if (ex.status === 403) {
            throw new Error(ex.data?.error || "Ссылка недействительна");
          }
          throw new Error(ex.data?.error || "Ошибка гостевой сессии");
        }

        try {
          const data = await apiFetch<BoardLoadResponse>(boardUrl(null));
          const useCred = data.access === "guest_session" || data.access === "session";
          mountBoard(data, { shareToken: null, useCredentials: useCred });
        } catch (e) {
          if (e instanceof ApiError && e.status === 404) {
            // Доска, добавленная по ссылке: прав по аккаунту нет, но человек
            // держит её у себя. Сервер помнит, по какой ссылке он вошёл, —
            // просим открыть по ней, без адреса со ссылкой.
            const byShortcut = await fetch(
              `/api/v1/boards/${encodeURIComponent(id)}/guest-session`,
              {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ use_shortcut: true }),
              }
            ).then((r) => r.json().then((data) => ({ status: r.status, data })))
              .catch(() => null);
            if (byShortcut && byShortcut.status === 201) {
              mountBoard(byShortcut.data as BoardLoadResponse, {
                shareToken: null,
                useCredentials: true,
              });
              return;
            }
            await mountAdminSpectate(true);
            return;
          }
          throw e;
        }
      } catch (e) {
        if (cancelled) return;
        const msg = boardLoadErrorMessage(e);
        if (msg === "Unauthorized" || (e as { status?: number }).status === 401) {
          navigate(authEntryPathForError(e, `/board/${id}${location.search}`), {
            replace: true,
          });
          return;
        }
        flash.push("error", msg);
      }
    }

    void loadFlow();

    return () => {
      cancelled = true;
      presenceRef.current?.destroy();
      sceneRef.current?.destroy();
      presenceRef.current = null;
      sceneRef.current = null;
    };
    // Только смена id доски. Иначе сцена уничтожается снова и снова → мигание.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!ready) return;
    sceneRef.current?.setActiveTool(tool, false);
  }, [tool, ready]);

  const handleToolChange = useCallback((next: BoardToolId) => {
    setTool(next);
    // force: повторный выбор фигуры после авто-switch на select
    sceneRef.current?.setActiveTool(next, true);
  }, []);

  useEffect(() => {
    function onOpenComments(ev: Event) {
      const ce = ev as CustomEvent<{ objectId?: string }>;
      const id = ce.detail?.objectId;
      if (id) setCommentObjectId(id);
    }
    window.addEventListener("wb:open-object-comments", onOpenComments as EventListener);
    return () =>
      window.removeEventListener("wb:open-object-comments", onOpenComments as EventListener);
  }, []);

  useEffect(() => {
    function onSelectionDrag(ev: Event) {
      const ce = ev as CustomEvent<boolean>;
      setIsSelectionDragging(Boolean(ce.detail));
    }
    window.addEventListener("wb:selection-drag", onSelectionDrag as EventListener);
    return () => window.removeEventListener("wb:selection-drag", onSelectionDrag as EventListener);
  }, []);

  useSpaceGesture({
    enabled: canEdit && ready,
    activeTool: tool,
    onToolChange: handleToolChange,
  });

  useBoardToolShortcuts({
    enabled: canEdit && ready,
    activeTool: tool,
    onToolChange: handleToolChange,
    selectedIds,
    onDeleteSelection: () => void sceneRef.current?.deleteSelected(),
    onUndo: () => void sceneRef.current?.undo(),
    onRedo: () => void sceneRef.current?.redo(),
    onDuplicate: () => void sceneRef.current?.duplicateSelection(),
    onCopySelection: () => sceneRef.current?.copySelection(),
    onToggleLineAxisSnap: () => {
      const on = !lineAxisSnapEnabled();
      setLineAxisSnapEnabled(on);
      flash.push(
        "info",
        on
          ? "Прямые снова прилипают к горизонтали и вертикали (G — выключить)"
          : "Прилипание выключено: прямые ложатся под любым углом (G — включить)"
      );
    },
    onCopyStyle: () => flash.push("info", copyStyleFromSelection(sceneRef.current, selectedIds)),
    onPasteStyle: () => {
      const msg = pasteStyleToSelection(sceneRef.current, selectedIds);
      if (msg) flash.push("info", msg);
    },
    onPaste: () => void sceneRef.current?.pasteClipboard(),
    onZOrderForward: () => void sceneRef.current?.adjustZOrder("front"),
    onZOrderBackward: () => void sceneRef.current?.adjustZOrder("back"),
    // Смена вида линии незаметна на панели, поэтому называем её словом.
    onCyclePenDash: () => void cycleActivePenDash(),
    onEditSelection: (id) => {
      const scene = sceneRef.current;
      const obj = scene?.findObject(id);
      // Только то, что правится в поле набора: надпись, стикер, фрейм.
      if (!scene || !obj || obj.locked || !isOverlayEditableObject(obj)) return;
      scene.requestTextEdit(id);
    },
    onNudgeSelection: (dx, dy) => void sceneRef.current?.nudgeSelection(dx, dy),
    onGroupSelection: () => void sceneRef.current?.groupSelection?.(),
    onUngroupSelection: () => void sceneRef.current?.ungroupSelection?.(),
    onImportPdf: canEdit ? () => pdfInputRef.current?.click() : undefined,
  });

  const objects = ready ? sceneRef.current?.getObjects() : null;
  const boardIsEmpty = Boolean(objects && objects.length === 0);
  const canUndo = ready && Boolean(sceneRef.current?.canUndo());
  const canRedo = ready && Boolean(sceneRef.current?.canRedo());

  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    const t = window.setTimeout(() => {
      if (cancelled) return;
      presenceRef.current?.notifyViewportChange();
    }, 80);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [ready, vp.viewport.panX, vp.viewport.panY, vp.viewport.scale]);

  useEffect(() => {
    if (ready) {
      vp.centerOnOrigin();
      vp.viewportRef.current?.focus();
    }
  }, [ready, vp.centerOnOrigin, vp.viewportRef]);

  const handleImageFile = useCallback(
    (file: File) => {
      const scene = sceneRef.current;
      if (!scene || !canEdit) return;
      const pt = scene.stageCenterPoint();
      void scene.uploadImageFile(file, pt.x, pt.y);
    },
    [canEdit]
  );

  const handlePdfFile = useCallback(
    (file: File) => {
      const scene = sceneRef.current;
      if (!scene || !canEdit) return;
      const pt = scene.stageCenterPoint();
      setPdfImportMessage("Загрузка…");
      void scene
        .uploadPdfFile(file, pt.x, pt.y, (p) => {
          setPdfImportMessage(presentationProgressLabel(p));
        })
        .then(() => setPdfImportMessage(null))
        .catch(() => setPdfImportMessage(null));
    },
    [canEdit]
  );

  const handleAudioFile = useCallback(
    (file: File) => {
      const scene = sceneRef.current;
      if (!scene || !canEdit) return;
      const pt = scene.stageCenterPoint();
      void scene.uploadAudioFile(file, pt.x, pt.y).then(() => {
        scene.setActiveTool("select", true);
        setTool("select");
      });
    },
    [canEdit]
  );

  const handleInsertWidget = useCallback(
    (templateId: BoardTemplateId) => {
      const scene = sceneRef.current;
      if (!scene || !canEdit) return;
      const pt = scene.stageCenterPoint();
      const objects = buildWidgetObjectsAtCenter(
        templateId,
        pt.x,
        pt.y,
        scene.getObjects() as BoardObjectV2[]
      );
      if (!objects.length) return;
      void scene
        .addObjectsBatch(objects, `widget-${templateId}-${Date.now()}`)
        .then(() => {
          scene.setSelection(objects.map((o) => o.id));
          scene.setActiveTool("select", true);
          setTool("select");
        });
    },
    [canEdit]
  );

  const handleInsertTimer = useCallback(() => {
    const scene = sceneRef.current;
    if (!scene || !canEdit) return;
    const pt = scene.stageCenterPoint();
    void scene.addTimerAt(pt.x, pt.y).then(() => {
      scene.setActiveTool("select", true);
      setTool("select");
    });
  }, [canEdit]);

  const handleInsertCalculator = useCallback(() => {
    const scene = sceneRef.current;
    if (!scene || !canEdit) return;
    const pt = scene.stageCenterPoint();
    void scene.addCalculatorAt(pt.x, pt.y, { variant: "calc" }).then(() => {
      scene.setActiveTool("select", true);
      setTool("select");
    });
  }, [canEdit]);

  const handleInsertGraphUnits = useCallback(() => {
    const scene = sceneRef.current;
    if (!scene || !canEdit) return;
    const pt = scene.stageCenterPoint();
    void scene.addCalculatorAt(pt.x, pt.y, { variant: "graph_units" }).then(() => {
      scene.setActiveTool("select", true);
      setTool("select");
    });
  }, [canEdit]);

  const handleInsertCode = useCallback(() => {
    const scene = sceneRef.current;
    if (!scene || !canEdit) return;
    const pt = scene.stageCenterPoint();
    void scene.addCodeAt(pt.x, pt.y).then(() => {
      scene.setActiveTool("select", true);
      setTool("select");
    });
  }, [canEdit]);

  const handleInsertSql = useCallback(() => {
    const scene = sceneRef.current;
    if (!scene || !canEdit) return;
    const pt = scene.stageCenterPoint();
    void scene.addSqlAt(pt.x, pt.y).then(() => {
      scene.setActiveTool("select", true);
      setTool("select");
    });
  }, [canEdit]);

  useEffect(() => {
    if (!canEdit || !ready) return;
    function switchToSelect() {
      const scene = sceneRef.current;
      if (!scene) return;
      scene.setActiveTool("select", true);
      setTool("select");
    }
    function onPaste(ev: ClipboardEvent) {
      if (isTypingTarget(ev.target)) return;
      const scene = sceneRef.current;
      if (!scene) return;
      handleBoardClipboardPaste(ev, scene, { switchToSelect });
    }
    function onCopy(ev: ClipboardEvent) {
      if (isTypingTarget(ev.target)) return;
      handleBoardClipboardCopy(ev, sceneRef.current);
    }
    window.addEventListener("paste", onPaste);
    window.addEventListener("copy", onCopy);
    return () => {
      window.removeEventListener("paste", onPaste);
      window.removeEventListener("copy", onCopy);
    };
  }, [canEdit, ready]);

  function onViewportDrop(ev: React.DragEvent) {
    if (!canEdit) return;
    ev.preventDefault();
    const file = ev.dataTransfer.files?.[0];
    if (!file) return;
    const scene = sceneRef.current;
    if (!scene) return;
    const pt = vp.mapClientToStage(ev.clientX, ev.clientY);
    if (isPresentationFile(file)) {
      setPdfImportMessage("Загрузка…");
      void scene
        .uploadPdfFile(file, pt.x, pt.y, (p) => {
          setPdfImportMessage(presentationProgressLabel(p));
        })
        .then(() => setPdfImportMessage(null))
        .catch(() => setPdfImportMessage(null));
      return;
    }
    if (isBoardAudioFile(file)) {
      void scene.uploadAudioFile(file, pt.x, pt.y).then(() => {
        scene.setActiveTool("select", true);
        setTool("select");
      });
      return;
    }
    if (!file.type.startsWith("image/")) return;
    void scene.uploadImageFile(file, pt.x, pt.y);
  }

  function onViewportDragOver(ev: React.DragEvent) {
    if (!canEdit) return;
    if (ev.dataTransfer.types.includes("Files")) {
      ev.preventDefault();
    }
  }

  useEffect(() => {
    if (!ready) return;
    // Рамка выделения живёт под CSS-камерой — на pan/zoom её не пересобираем.
    // Опросы слушают wb:live-viewport; здесь — только fixed-оверлеи текста/комментов.
    const needsOverlayTick = Boolean(textEditId || commentObjectId);
    if (!needsOverlayTick) return;
    const t = window.setTimeout(() => {
      setChromeLayoutTick((n) => n + 1);
    }, 320);
    return () => clearTimeout(t);
  }, [ready, vp.viewport.panX, vp.viewport.panY, vp.viewport.scale, textEditId, commentObjectId]);

  const runExportPng = useCallback(
    async (mode: "viewport" | "selection") => {
      const scene = sceneRef.current;
      const vpEl = vp.viewportRef.current;
      if (!scene || !vpEl || exportBusy) return;

      const objects = scene.getObjects();
      let bounds =
        mode === "selection"
          ? boundsForSelection(objects, selectedIds)
          : boundsForViewport(vpEl.getBoundingClientRect(), vp.viewport.panX, vp.viewport.panY, vp.viewport.scale);

      if (!bounds || bounds.w < 1 || bounds.h < 1) {
        flash.push("error", "Нечего экспортировать");
        return;
      }

      setExportBusy(true);
      try {
        const blob = await exportBoardPng({
          objects,
          bounds,
          assetContentUrl: (assetId) => scene.assetContentUrl(assetId),
          credentials: scene.useCredentials ? "include" : "omit",
        });
        downloadBlob(blob, exportFilename(title));
        flash.push("ok", "PNG сохранён");
      } catch (e) {
        flash.push("error", e instanceof Error ? e.message : "Ошибка экспорта");
      } finally {
        setExportBusy(false);
      }
    },
    [exportBusy, selectedIds, title, vp.viewport.panX, vp.viewport.panY, vp.viewport.scale, vp.viewportRef, flash]
  );

  const runExportLessonPdf = useCallback(async () => {
    const scene = sceneRef.current;
    if (!scene || exportBusy) return;
    setExportBusy(true);
    try {
      const { exportLessonAsPdf, downloadFramePdf } = await import("../lib/board/exportFramePdf");
      const result = await exportLessonAsPdf({
        objects: scene.getObjects(),
        boardTitle: title,
        assetContentUrl: (assetId) => scene.assetContentUrl(assetId),
        credentials: scene.useCredentials ? "include" : "omit",
      });
      downloadFramePdf(result.blob, result.filename);
      flash.push(
        "ok",
        result.pageCount > 1
          ? `PDF урока сохранён (${result.pageCount} стр.)`
          : "PDF урока сохранён"
      );
    } catch (e) {
      flash.push("error", e instanceof Error ? e.message : "Не удалось сохранить PDF урока");
    } finally {
      setExportBusy(false);
    }
  }, [exportBusy, title, flash]);

  const navigateToFrame = useCallback(
    (frameId: string, opts?: { select?: boolean }) => {
      const scene = sceneRef.current;
      if (!scene) return;
      follow.clearFollow();
      const obj = scene.findObject(frameId);
      if (!obj || obj.type !== "frame") return;
      const w = typeof obj.w === "number" && obj.w > 0 ? obj.w : 640;
      const h = typeof obj.h === "number" && obj.h > 0 ? obj.h : 400;
      const x = typeof obj.x === "number" ? obj.x : 0;
      const y = typeof obj.y === "number" ? obj.y : 0;
      const select = opts?.select !== false;
      if (select && scene.canEdit) {
        scene.setActiveTool("select", true);
        setTool("select");
        scene.setSelection([frameId]);
      } else {
        scene.setSelection([]);
      }
      // На телефоне меньше полей — фрейм крупнее на экране.
      const pad = window.matchMedia("(max-width: 720px)").matches ? 28 : 72;
      vp.fitToWorldBounds({ x, y, w, h }, pad);
      vp.viewportRef.current?.focus();
    },
    [follow, vp]
  );

  const framePresent = useFramePresentation({
    presenceRef,
    sceneRef,
    canPresent: canEdit,
    ready,
    navigateToFrame,
  });
  const boardVote = useBoardVote({ presenceRef, canHost: canEdit, myUserId });

  /**
   * Опрос живёт не объектом сцены, а поверх доски: его видят все, кто сейчас
   * на занятии, и он уходит вместе с ним. Поэтому ставится не через
   * addObject, а через канал присутствия.
   */
  const handleInsertPoll = useCallback(() => {
    const scene = sceneRef.current;
    const pt = scene ? scene.stageCenterPoint() : null;
    // Опрос встаёт по центру экрана: его место — левый верхний угол, а
    // размер задан в точках экрана, поэтому сдвигаем на половину размера.
    const scale = vp.getLiveViewport().scale || 1;
    const at = pt
      ? {
          worldX: pt.x - VOTE_WIDTH_DEFAULT / 2 / scale,
          worldY: pt.y - VOTE_HEIGHT_DEFAULT / 2 / scale,
        }
      : undefined;
    if (boardVote.startOrQueue(at) === "queued") {
      flash.push("info", "Доска переподключается — опрос появится, как только связь восстановится");
    }
  }, [boardVote, flash, vp]);
  onSignalRef.current = (ev) => {
    framePresent.handleSignal(ev);
    boardVote.handleSignal(ev);
    lessonSignalRef.current?.(ev);
  };
  onPeerJoinedRef.current = () => boardVote.onPeerJoined();
  onSubscribedRef.current = () => boardVote.onSubscribed();

  const presentFrameTitle = (() => {
    if (!framePresent.state.active) return undefined;
    const id = framePresent.state.frameIds[framePresent.state.index];
    if (!id) return undefined;
    const obj = sceneRef.current?.findObject(id);
    return obj && isFrameObject(obj) ? frameTitle(obj) : undefined;
  })();


  const wsHint =
    wsState === "subscribed"
      ? null
      : wsState === "error"
        ? wsDetail === "forbidden"
          ? "нет доступа"
          : "ошибка"
        : wsState === "replaced"
          ? "доска открыта в другой вкладке"
          : wsState === "reconnecting"
            ? "переподключение"
            : "offline";

  return (
    <div className="wb-board-shell" data-testid="board-shell">
      <header
        ref={toolbarRef}
        className={`wb-board-toolbar${compactHeader ? " is-compact" : ""}`}
      >
        <h1 className="wb-board-toolbar__title">{title}</h1>
        <div className="wb-board-toolbar__actions">
          <button
            type="button"
            className="wb-btn wb-btn--ghost wb-btn--sm"
            title={description || "Название и описание доски"}
            onClick={() => setInfoOpen(true)}
          >
            О доске
          </button>
          {shareLink.canCopy ? (
            <BoardCopyShareLinkButton
              className="wb-btn wb-btn--ghost wb-btn--sm wb-board-toolbar__action--wide-only"
              busy={shareLink.busy}
              text={shareLink.text}
              onCopy={() => void shareLink.copyShareLink()}
            />
          ) : null}
          <BoardFramesButton
            className="wb-btn wb-btn--ghost wb-btn--sm"
            ready={ready}
            sceneRef={sceneRef}
            sceneTick={historyTick}
            selectedIds={selectedIds}
            onNavigate={navigateToFrame}
            presentation={
              canEdit && ready
                ? {
                    canControl: true,
                    active: framePresent.state.active,
                    isPresenter: framePresent.state.isPresenter,
                    disabled: framePresent.state.active && !framePresent.state.isPresenter,
                    onToggle: () => {
                      if (framePresent.state.active && framePresent.state.isPresenter) {
                        framePresent.stop();
                        return;
                      }
                      if (!framePresent.start()) {
                        flash.push("error", "Сначала добавьте хотя бы один фрейм на доску");
                      }
                    },
                  }
                : undefined
            }
          />
          {canEdit && ready && !isFreeExternal ? (
            <button
              type="button"
              className="wb-btn wb-btn--ghost wb-btn--sm"
              onClick={() => setCheckpointsOpen(true)}
              title="Сохранить и вернуть состояния доски"
            >
              Снимки
            </button>
          ) : null}
          {canEdit ? (
            <button
              type="button"
              className="wb-btn wb-btn--ghost wb-btn--sm wb-board-toolbar__action--wide-only"
              onClick={() => setSettingsOpen(true)}
              title="Настройки — горячие клавиши"
            >
              Настройки
            </button>
          ) : null}
          {canEdit ? (
            <button
              type="button"
              className="wb-btn wb-btn--ghost wb-btn--sm wb-board-toolbar__action--wide-only"
              title="Пройти по доске и попробовать инструменты"
              onClick={() => setTourOpen(true)}
            >
              {/* Звёздочка стоит всегда: кнопку просили заметную,
                  а не исчезающую после первого прохода. */}
              Обучение ✨
            </button>
          ) : null}
          <BoardHelpButton
            className="wb-btn wb-btn--ghost wb-btn--sm wb-board-toolbar__action--wide-only"
            open={helpOpen}
            onOpenChange={setHelpOpen}
          />
          {isBoardOwner && ready && !schoolLesson ? (
            <button
              type="button"
              className="wb-btn wb-btn--sm wb-board-end-session-btn"
              title="Сохранить урок и выйти в кабинет"
              onClick={() => {
                // Открытое поле набора сначала закрываем — его текст сохранится.
                setTextEditId(null);
                setEndSessionOpen(true);
              }}
            >
              <span className="wb-board-end-session-btn__long">Завершить сессию</span>
              <span className="wb-board-end-session-btn__short">Завершить</span>
            </button>
          ) : null}
          <BoardPageMenu
            exportBusy={exportBusy}
            hasSelection={selectedIds.length > 0}
            onExportViewport={ready ? () => void runExportPng("viewport") : undefined}
            onExportSelection={ready ? () => void runExportPng("selection") : undefined}
            onExportLessonPdf={ready ? () => void runExportLessonPdf() : undefined}
            showShareLinkInMenu={shareLink.canCopy}
            shareLinkBusy={shareLink.busy}
            shareLinkLabel={shareLink.text}
            onCopyShareLink={() => void shareLink.copyShareLink()}
            onOpenHelp={() => setHelpOpen(true)}
            onOpenSettings={() => setSettingsOpen(true)}
            showCheckpoints={Boolean(canEdit && ready && !isFreeExternal)}
            onOpenCheckpoints={() => setCheckpointsOpen(true)}
            onOpenBugReport={() => setBugReportOpen(true)}
            homeHref={accountKind === "school" ? "/school" : "/"}
          />
        </div>
      </header>

      {/* Реклама школы — только хозяину доски на бесплатном тарифе.
          Приглашённым её не показываем вовсе: человек пришёл на чужой урок,
          а не на витрину. Из-за прежнего правила «гость по ссылке» полоска
          вылезала и на школьных досках — у школы свои ученики, реклама
          другой школы там неуместна. */}
      {/* Конец урока школы — первым: в конце урока это важнее всего.
          Хозяину доски — «Завершить занятие» с итогами, ученику (пришёл по
          ссылке урока) — «Завершить урок» с разрешения преподавателя. */}
      {id ? (
        <BoardLessonFinishStrip
          boardId={id}
          role={!ready ? null : isBoardOwner ? "teacher" : shareToken ? "student" : null}
          shareToken={shareToken}
          onLesson={(lesson) => setSchoolLesson(Boolean(lesson))}
          sendSignal={(kind, payload) => presenceRef.current?.sendSignal?.(kind, payload) ?? false}
          signalBus={lessonSignalRef}
          beforeLeave={async () => {
            // Даём закрывшемуся полю набора отправить текст, потом ждём сервер.
            setTextEditId(null);
            await new Promise((r) => setTimeout(r, 300));
            await sceneRef.current?.whenSaved(8000);
          }}
        />
      ) : null}

      {/* Обучение зовём первым: человеку, который впервые открыл доску,
          оно нужнее любой рекламы. Полоска закрывается насовсем. */}
      <BoardTourPromoStrip
        visible={isFreeExternal && isBoardOwner}
        onStart={() => setTourOpen(true)}
      />

      <BoardSchoolPromoStrip visible={isFreeExternal && isBoardOwner} />

      <BoardBugReportDialog
        open={bugReportOpen}
        onOpenChange={setBugReportOpen}
        boardId={id}
        defaultReporterName={
          participants.find((p) => p.isSelf)?.displayLabel ?? ""
        }
        context={{
          canEdit,
          wsState,
          accountKind,
          planCode,
          tool,
          accessMode,
          sessionAccess,
          hasShareToken: Boolean(shareToken),
        }}
        onSuccess={(reportId) =>
          flash.push(
            "ok",
            reportId ? `Сообщение отправлено (№ ${reportId})` : "Сообщение отправлено"
          )
        }
        onError={(msg) => flash.push("error", msg)}
      />

      <BoardSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />

      {canEdit && !isFreeExternal ? (
        <BoardCheckpointsPanel
          open={checkpointsOpen}
          boardId={id!}
          busy={checkpointBusy}
          onClose={() => setCheckpointsOpen(false)}
          onSave={saveCheckpoint}
          onDelete={sessionAccess ? deleteCheckpoint : undefined}
          pendingRestore={pendingRestore}
          restoreSaveTitle={restoreSaveTitle}
          onRestoreSaveTitleChange={setRestoreSaveTitle}
          onConfirmRestoreSave={() => void confirmRestoreWithSave()}
          onConfirmRestoreSkip={() => void confirmRestoreSkip()}
          onCancelRestore={() => {
            setPendingRestore(null);
            setRestoreSaveTitle("");
          }}
          onRequestRestore={(item) => {
            setCheckpointsOpen(false);
            setPendingRestore(item);
            setRestoreSaveTitle("");
          }}
        />
      ) : null}

      <div className="wb-board-workspace">
        <BoardCallSplitLayout
          callEmbedUrl={callSession.embedUrl}
          callStatus={callSession.status}
          onRetryCall={callSession.retry}
          initialOpen={callOpenParam}
          allowCall={!isFreeExternal}
        >
        <main
          ref={boardMainRef}
          className={`wb-board-main${!canEdit && ready ? " wb-board-main--view-only" : ""}`}
        >
          <BoardFramePresentBar
            active={framePresent.state.active}
            isPresenter={framePresent.state.isPresenter}
            presenterLabel={framePresent.state.presenterLabel}
            index={framePresent.state.index}
            total={framePresent.state.frameIds.length}
            frameTitle={presentFrameTitle}
            onPrev={framePresent.prev}
            onNext={framePresent.next}
            onStop={framePresent.stop}
          />
          <BoardVotePanel
            blocks={boardVote.blocks}
            countsFor={boardVote.countsFor}
            viewportRef={vp.viewportRef}
            viewport={vp.viewport}
            getLiveViewport={vp.getLiveViewport}
            layoutTick={chromeLayoutTick}
            canManage={canEdit}
            isOwnerOf={boardVote.isOwnerOf}
            onCast={boardVote.cast}
            onEnd={boardVote.end}
            onMove={boardVote.move}
            onResize={boardVote.resize}
            onTitleChange={boardVote.setTitle}
            onOptionsChange={boardVote.setOptions}
          />
          <div className="wb-board-status" aria-live="polite">
            <BoardCollaborationBar
              boardId={id!}
              sessionAccess={sessionAccess}
              participants={participants}
              wsHint={wsHint}
              followingPeerId={follow.followPeerId}
              onFollow={ready ? follow.startFollow : undefined}
              onUnfollow={follow.followPeerId ? follow.clearFollow : undefined}
              panelOpen={accessPanelOpen}
              onPanelOpenChange={setAccessPanelOpen}
            />
            {ready && adminSpectate ? (
              <span
                className="wb-view-only-chip"
                title="Вы на доске невидимо: участники вас не видят, менять ничего нельзя"
              >
                Невидимый просмотр
              </span>
            ) : ready && !canEdit ? (
              <span className="wb-view-only-chip" title="Объекты двигать нельзя — можно листать и зумить доску">
                Только просмотр
              </span>
            ) : null}
          </div>

          <BoardFloatingToolbar
            tool={tool}
            onToolChange={handleToolChange}
            canEdit={canEdit}
            canUndo={canUndo}
            canRedo={canRedo}
            onUndo={() => void sceneRef.current?.undo()}
            onRedo={() => void sceneRef.current?.redo()}
            onPaste={canEdit ? () => void sceneRef.current?.pasteClipboard() : undefined}
            onImageFile={handleImageFile}
            onPdfFile={canEdit ? handlePdfFile : undefined}
            onAudioFile={canEdit ? handleAudioFile : undefined}
            onVoiceError={canEdit ? (msg) => flash.push("error", msg) : undefined}
            onInsertWidget={canEdit ? handleInsertWidget : undefined}
            onInsertTimer={canEdit ? handleInsertTimer : undefined}
            onInsertCalculator={canEdit ? handleInsertCalculator : undefined}
            onInsertGraphUnits={canEdit ? handleInsertGraphUnits : undefined}
            onInsertCode={canEdit ? handleInsertCode : undefined}
            onInsertSql={canEdit ? handleInsertSql : undefined}
            onInsertPoll={canEdit ? handleInsertPoll : undefined}
            pdfInputRef={pdfInputRef}
          />

          <div className="wb-canvas-wrap">
            <BoardEmptyHint visible={boardIsEmpty} canEdit={canEdit} />
            <div
              ref={vp.viewportRef}
              className={`wb-board-viewport${pdfPresentation.activePresentationPdfId ? " is-presenting" : ""}`}
              style={vp.viewportStyle}
              tabIndex={0}
              onDrop={onViewportDrop}
              onDragOver={onViewportDragOver}
              {...vp.handlers}
            >
              <canvas className="wb-board-grid-canvas" aria-hidden="true" />
              <div className="wb-board-viewport__inner" style={vp.innerStyle}>
                <div className="wb-board-world">
                  <div
                    ref={stageRef}
                    className="wb-board-stage"
                    data-testid="board-stage"
                    style={{ display: ready ? "block" : "none" }}
                  />
                </div>
                <BoardSelectionToolbar
                  canEdit={canEdit}
                  activeTool={tool}
                  selectedIds={selectedIds}
                  suppressDuringDrag={framePresent.state.active || isSelectionDragging}
                  editingObjectId={textEditId}
                  sceneRef={sceneRef}
                  viewportRef={vp.viewportRef}
                  viewport={vp.viewport}
                  getLiveViewport={vp.getLiveViewport}
                  layoutTick={chromeLayoutTick}
                  onPresentPdf={canEdit ? pdfPresentation.setLocalPresentationPdfId : undefined}
                  onBroadcastPdf={
                    canEdit
                      ? (objectId) => {
                          void sceneRef.current?.startPdfBroadcast(objectId);
                        }
                      : undefined
                  }
                  onDownloadPresentation={handleDownloadPresentation}
                  exportBusy={exportBusy}
                  onOpenComments={(objectId) => setCommentObjectId(objectId)}
                  onExportFramePdf={async (objectId) => {
                    const scene = sceneRef.current;
                    if (!scene || exportBusy) return;
                    const frame = scene.findObject(objectId);
                    if (!frame || frame.type !== "frame") return;
                    setExportBusy(true);
                    try {
                      const { exportFrameAsPdf, downloadFramePdf } = await import(
                        "../lib/board/exportFramePdf"
                      );
                      const result = await exportFrameAsPdf({
                        frame,
                        objects: scene.getObjects(),
                        assetContentUrl: (assetId) => scene.assetContentUrl(assetId),
                        credentials: scene.useCredentials ? "include" : "omit",
                      });
                      downloadFramePdf(result.blob, result.filename);
                      flash.push("ok", "PDF рамки сохранён");
                    } catch (e) {
                      flash.push(
                        "error",
                        e instanceof Error ? e.message : "Не удалось сохранить PDF"
                      );
                    } finally {
                      setExportBusy(false);
                    }
                  }}
                />
              </div>
            </div>
          </div>

          {minimapVisible ? (
            <BoardMinimap
              ready={ready}
              objects={ready && sceneRef.current ? sceneRef.current.getObjects() : []}
              sceneTick={historyTick}
              viewport={vp.viewport}
              getLiveViewport={vp.getLiveViewport}
              viewportRef={vp.viewportRef}
              onViewportChange={vp.setViewportState}
              onUserNavigate={() => follow.clearFollow()}
            />
          ) : null}

          <BoardZoomControls
            scale={vp.viewport.scale}
            onZoomIn={() => {
              follow.clearFollow();
              vp.zoomIn();
            }}
            onZoomOut={() => {
              follow.clearFollow();
              vp.zoomOut();
            }}
            onReset={() => {
              follow.clearFollow();
              vp.resetView();
            }}
            onFitContent={() => {
              const scene = sceneRef.current;
              if (!scene) return;
              follow.clearFollow();
              const bounds = contentBoundsForFit(scene.getObjects());
              if (!bounds) {
                vp.resetView();
                return;
              }
              vp.fitToWorldBounds(bounds, 64);
            }}
            canFitContent={ready}
            canZoomIn={vp.canZoomIn}
            canZoomOut={vp.canZoomOut}
            minimapVisible={minimapVisible}
            onToggleMinimap={() => {
              setMinimapVisible((v) => {
                const next = !v;
                writeMinimapVisible(next);
                return next;
              });
            }}
          />
        </main>
        </BoardCallSplitLayout>
      </div>

      <div className="wb-board-flash">
        <FlashStack items={flash.items} onDismiss={flash.dismiss} />
      </div>

      {textEditId && sceneRef.current ? (
        <TextOverlayEditor
          objectId={textEditId}
          scene={sceneRef.current}
          viewportRef={vp.viewportRef}
          viewport={vp.viewport}
          getLiveViewport={vp.getLiveViewport}
          layoutTick={chromeLayoutTick}
          onClose={() => setTextEditId(null)}
          onViewportChange={vp.setViewportState}
        />
      ) : null}

      {commentObjectId && sceneRef.current && !isSelectionDragging ? (
        <ObjectCommentsPanel
          objectId={commentObjectId}
          scene={sceneRef.current}
          viewportRef={vp.viewportRef}
          viewport={vp.viewport}
          layoutTick={chromeLayoutTick}
          sceneTick={historyTick}
          canEdit={canEdit}
          authorLabel={
            participants.find((p) => p.isSelf)?.displayLabel ?? "Участник"
          }
          authorPeerId={presenceRef.current?.myPeerId ?? null}
          onClose={() => setCommentObjectId(null)}
        />
      ) : null}

      {pdfPresentation.activePresentationPdfId && sceneRef.current ? (
        <PresentationOverlay
          objectId={pdfPresentation.activePresentationPdfId}
          scene={sceneRef.current}
          sceneTick={pdfPresentation.sceneTick}
          canEdit={canEdit}
          viewportRef={vp.viewportRef}
          onCloseLocal={() => pdfPresentation.setLocalPresentationPdfId(null)}
          onDownloaded={() => flash.push("ok", "Презентация сохранена")}
          onError={(msg) => flash.push("error", msg)}
        />
      ) : null}

      {infoOpen && id ? (
        <BoardInfoDialog
          boardId={id}
          title={title}
          description={description}
          canEdit={canEditInfo}
          onSaved={(next) => {
            setTitle(next.title);
            setDescription(next.description);
            document.title = `${next.title} — Bezpart Board`;
          }}
          onClose={() => setInfoOpen(false)}
        />
      ) : null}

      {endSessionOpen && id ? (
        <BoardEndSessionDialog
          boardId={id}
          title={title}
          description={description}
          canSnapshot={!isFreeExternal}
          waitSaved={async () => {
            // Даём закрывшемуся полю набора отправить текст, потом ждём сервер.
            await new Promise((r) => setTimeout(r, 300));
            return (await sceneRef.current?.whenSaved(8000)) ?? true;
          }}
          onSaved={(next) => {
            setTitle(next.title);
            setDescription(next.description);
          }}
          onDone={() => navigate("/cabinet")}
          onClose={() => setEndSessionOpen(false)}
        />
      ) : null}

      {tourOpen ? (
        <BoardTour scene={sceneRef.current} onClose={() => setTourOpen(false)} />
      ) : null}

      {pdfImportMessage ? <PdfImportProgress message={pdfImportMessage} /> : null}
    </div>
  );
}
